export const MORNING_A11Y_STORAGE_KEY = 'morning-accessibility'
export const MORNING_A11Y_CHANGE_EVENT = 'morning-accessibility-change'

export const TEXT_SIZE_STEPS = [90, 100, 110, 125] as const

export type AccessibilitySettings = {
  textSizeIndex: number
  highContrast: boolean
  reduceMotion: boolean
  highlightLinks: boolean
  readableFont: boolean
}

export const defaultAccessibilitySettings: AccessibilitySettings = {
  textSizeIndex: 1,
  highContrast: false,
  reduceMotion: false,
  highlightLinks: false,
  readableFont: false,
}

const CLASS_HIGH_CONTRAST = 'a11y-high-contrast'
const CLASS_REDUCE_MOTION = 'a11y-reduce-motion'
const CLASS_HIGHLIGHT_LINKS = 'a11y-highlight-links'
const CLASS_READABLE_FONT = 'a11y-readable-font'

function clampTextSizeIndex(index: number): number {
  if (!Number.isFinite(index)) return defaultAccessibilitySettings.textSizeIndex
  return Math.max(0, Math.min(TEXT_SIZE_STEPS.length - 1, Math.round(index)))
}

function normalizeSettings(
  value: Partial<AccessibilitySettings> | null | undefined,
): AccessibilitySettings {
  if (!value || typeof value !== 'object') {
    return { ...defaultAccessibilitySettings }
  }

  return {
    textSizeIndex: clampTextSizeIndex(
      typeof value.textSizeIndex === 'number'
        ? value.textSizeIndex
        : defaultAccessibilitySettings.textSizeIndex,
    ),
    highContrast: Boolean(value.highContrast),
    reduceMotion: Boolean(value.reduceMotion),
    highlightLinks: Boolean(value.highlightLinks),
    readableFont: Boolean(value.readableFont),
  }
}

export function readStoredAccessibility(): AccessibilitySettings {
  try {
    const raw = localStorage.getItem(MORNING_A11Y_STORAGE_KEY)
    if (!raw) return { ...defaultAccessibilitySettings }
    return normalizeSettings(JSON.parse(raw) as Partial<AccessibilitySettings>)
  } catch {
    return { ...defaultAccessibilitySettings }
  }
}

export function writeStoredAccessibility(settings: AccessibilitySettings) {
  try {
    localStorage.setItem(
      MORNING_A11Y_STORAGE_KEY,
      JSON.stringify(normalizeSettings(settings)),
    )
  } catch {
    /* ignore */
  }
}

function dispatchAccessibilityChange(settings: AccessibilitySettings) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent<AccessibilitySettings>(MORNING_A11Y_CHANGE_EVENT, {
      detail: normalizeSettings(settings),
    }),
  )
}

/** Syncs accessibility classes / CSS vars on `<html>`. */
export function applyAccessibilityToDocument(settings: AccessibilitySettings) {
  if (typeof document === 'undefined') return

  const next = normalizeSettings(settings)
  const root = document.documentElement
  const scale = (TEXT_SIZE_STEPS[next.textSizeIndex] ?? 100) / 100

  root.style.setProperty('--a11y-text-scale', String(scale))
  root.classList.toggle(CLASS_HIGH_CONTRAST, next.highContrast)
  root.classList.toggle(CLASS_REDUCE_MOTION, next.reduceMotion)
  root.classList.toggle(CLASS_HIGHLIGHT_LINKS, next.highlightLinks)
  root.classList.toggle(CLASS_READABLE_FONT, next.readableFont)
}

/** Persist, apply to the document, and notify other panel instances. */
export function commitAccessibilitySettings(settings: AccessibilitySettings) {
  const next = normalizeSettings(settings)
  writeStoredAccessibility(next)
  applyAccessibilityToDocument(next)
  dispatchAccessibilityChange(next)
  return next
}

/** OS reduced-motion preference or the user accessibility toggle. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false

  try {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return true
    }
  } catch {
    /* ignore */
  }

  return document.documentElement.classList.contains(CLASS_REDUCE_MOTION)
}
